import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Skeleton, notification } from "antd";
import { EmailList } from "../../components/mail/EmailList";
import mailClientAPI from "../../api/mail-client-api";
import { useUser } from "../../context/UserContext";

function MailboxView() {
  const { path, labelId: labelIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useUser();
  const { mailboxes, loading: mailboxesLoading, searchQuery, openDraft, registerDraftCallbacks, labels = [], updateMailboxCounts } = useOutletContext() || {};
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  // Track the current mailbox path for which we're expecting a response
  // This prevents race conditions when switching mailboxes quickly
  const currentMailboxRef = useRef(null);

  // Check if we're on a search route
  const isSearchRoute = location.pathname.endsWith("/search");
  const searchParams = new URLSearchParams(location.search);
  
  // Check if we're on a label route (check if pathname contains /label/ or if labelId param exists)
  const isLabelRoute = location.pathname.includes("/mailboxes/label/") || !!labelIdParam;
  const labelId = labelIdParam ? parseInt(labelIdParam) : (isLabelRoute ? parseInt(location.pathname.split("/label/")[1]?.split("/")[0]) : null);
  
  // Extract mailbox path - path already contains the mailbox name, not /search
  // For labels, we'll use INBOX as the default mailbox
  const mailboxPath = isLabelRoute ? "INBOX" : (path ? decodeURIComponent(path) : "INBOX");
  
  // Get search query for highlighting
  const searchQueryForHighlight = searchParams.get("q") || "";

  // Update draft in local state
  const handleDraftSaved = useCallback((draftInfo, oldDraftId = null) => {
    // Only update if we're viewing the drafts folder
    if (!mailboxPath.toLowerCase().includes("draft")) {
      return;
    }

    setEmails(prevEmails => {
      const { uid, mailbox, subject, to, cc, bcc, body, html, preview } = draftInfo;
      const draftUid = uid?.toString();
      const draftMailbox = mailbox;

      // If updating an existing draft, remove the old one first
      let filteredEmails = prevEmails;
      if (oldDraftId) {
        const oldUid = oldDraftId.toString();
        filteredEmails = prevEmails.filter(
          email => !((email.uid?.toString() === oldUid || email.id === oldUid) && 
                     email.folder?.toLowerCase().includes("draft"))
        );
      }

      // Check if this draft already exists (shouldn't happen, but handle it)
      const existingIndex = filteredEmails.findIndex(
        email => (email.uid?.toString() === draftUid || email.id === draftUid) && 
                 email.folder?.toLowerCase().includes("draft")
      );

      const draftEmail = {
        uid: parseInt(draftUid),
        id: draftUid,
        subject: subject || "(No Subject)",
        senderName: null,
        senderEmail: null,
        to: to || [],
        cc: cc || [],
        bcc: bcc || [],
        preview: preview || "",
        body: html || body || "",
        timestamp: new Date(),
        isRead: true, // Drafts are marked as seen
        isStarred: false,
        folder: draftMailbox?.toLowerCase() || mailboxPath.toLowerCase(),
        hasAttachments: false,
        attachments: [],
        labels: [],
        flags: new Set(["\\Draft", "\\Seen"]),
      };

      if (existingIndex >= 0) {
        // Update existing draft (shouldn't happen, but handle it)
        const updated = [...filteredEmails];
        updated[existingIndex] = { ...updated[existingIndex], ...draftEmail };
        return updated;
      } else {
        // Add new draft at the beginning (most recent first)
        return [draftEmail, ...filteredEmails];
      }
    });
  }, [mailboxPath]);

  // Remove draft from local state when deleted
  const handleDraftDeleted = useCallback((mailbox, emailId) => {
    const draftUid = emailId?.toString();
    const draftMailboxPath = mailbox || mailboxPath;
    
    // Remove draft from emails list
    setEmails(prevEmails => {
      return prevEmails.filter(
        email => {
          const emailUid = email.uid?.toString() || email.id?.toString();
          const emailFolder = email.folder?.toLowerCase() || "";
          // Remove if UID matches and it's in a draft folder
          return !(emailUid === draftUid && emailFolder.includes("draft"));
        }
      );
    });

    // Update mailbox counts to reduce draft count by 1
    if (updateMailboxCounts) {
      updateMailboxCounts({
        sourceFolder: draftMailboxPath,
        count: 1,
        wasUnread: false, // Drafts are typically marked as read
      });
    }
  }, [mailboxPath, updateMailboxCounts]);

  // Register draft update callbacks with parent (only when callbacks actually change)
  const callbacksRef = useRef({ onDraftSaved: null, onDraftDeleted: null });
  useEffect(() => {
    if (registerDraftCallbacks) {
      // Only register if callbacks have actually changed
      if (callbacksRef.current.onDraftSaved !== handleDraftSaved || 
          callbacksRef.current.onDraftDeleted !== handleDraftDeleted) {
        callbacksRef.current = {
          onDraftSaved: handleDraftSaved,
          onDraftDeleted: handleDraftDeleted,
        };
        registerDraftCallbacks(callbacksRef.current);
      }
    }
  }, [registerDraftCallbacks, handleDraftSaved, handleDraftDeleted]);

  // Redirect to INBOX if no path provided (but not if we're on a label route)
  useEffect(() => {
    // Don't redirect if we're on a label route
    if (isLabelRoute) {
      return;
    }
    // Only redirect if path is missing and we're not on a label route
    if (!path) {
      navigate("/mailboxes/INBOX", { replace: true });
    }
  }, [path, isLabelRoute, navigate]);

  // Load messages when mailbox path or search params change
  // Make it async and non-blocking - don't wait for mailboxes to load
  useEffect(() => {
    const loadMessages = async () => {
      // Set the current mailbox path we're loading
      const expectedMailboxPath = mailboxPath;
      const expectedIsSearch = isSearchRoute;
      const expectedIsLabel = isLabelRoute;
      const expectedLabelId = labelId;
      currentMailboxRef.current = expectedMailboxPath;
      setLoading(true);
      
      // Create searchParams inside the effect to avoid dependency issues
      const currentSearchParams = new URLSearchParams(location.search);
      
      try {
        let response;
        
        if (expectedIsSearch) {
          // Perform server-side search
          const searchParamsObj = {
            q: currentSearchParams.get("q") || "",
            from: currentSearchParams.get("from") || "",
            to: currentSearchParams.get("to") || "",
            subject: currentSearchParams.get("subject") || "",
          };
          const page = parseInt(currentSearchParams.get("p")) || 1;
          
          response = await mailClientAPI.searchMessages(expectedMailboxPath, searchParamsObj, page, 50);
        } else if (expectedIsLabel && expectedLabelId) {
          // Load messages filtered by label
          response = await mailClientAPI.getMessages(expectedMailboxPath, 1, 50, expectedLabelId);
        } else {
          // Regular message loading
          response = await mailClientAPI.getMessages(expectedMailboxPath, 1, 50);
        }
        
        // Only update state if this response is for the current mailbox
        // This prevents stale responses from overwriting newer data
        if (currentMailboxRef.current === expectedMailboxPath) {
          const messages = response.Output?.messages || [];
          // Transform messages to match expected format
          const transformedMessages = messages?.map?.((msg) => ({
            ...msg,
            id: msg.id || msg.uid?.toString() || msg.seqno?.toString(),
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(msg.date || Date.now()),
            // Preserve the original folder from API (important for label views where emails can be from any folder)
            // Only use expectedMailboxPath if folder is not provided by API
            folder: msg.folder || expectedMailboxPath.toLowerCase(),
            labels: msg.labels || [],
          }));
          console.log("Transformed messages:", transformedMessages);
          setEmails(transformedMessages || []);
        } else {
          console.log("Ignoring stale response for mailbox:", expectedMailboxPath);
        }
      } catch (error) {
        // Only update state on error if this is still the current mailbox
        if (currentMailboxRef.current === expectedMailboxPath) {
          console.error("Error loading messages:", error);
          setEmails([]);
        }
      } finally {
        // Only update loading state if this is still the current mailbox
        if (currentMailboxRef.current === expectedMailboxPath) {
          setLoading(false);
        }
      }
    };

    // Load messages immediately without waiting for mailboxes
    // Mailboxes loading is separate and doesn't block message loading
    loadMessages();
  }, [mailboxPath, isSearchRoute, isLabelRoute, labelId, location.search]);

  // Filter out sent drafts from drafts folder
  // A draft that has been sent will no longer have the \Draft flag
  const filteredEmails = useMemo(() => {
    // If we're viewing the drafts folder, filter out messages that don't have the isDraft flag
    if (mailboxPath.toLowerCase().includes("draft")) {
      return emails.filter(email => {
        // Keep the email if it has the isDraft flag set to true
        // If isDraft is undefined (for backwards compatibility), assume it's a draft
        return email.isDraft !== false;
      });
    }
    return emails;
  }, [emails, mailboxPath]);

  const handleSelectEmail = (email) => {
    // Check if this is a draft (has \Draft flag)
    const isDraft = email.flags?.has?.("\\Draft") || 
                    email.flags?.includes?.("\\Draft") || 
                    (Array.isArray(email.flags) && email.flags.includes("\\Draft")) ||
                    mailboxPath.toLowerCase().includes("draft");
    
    if (isDraft && openDraft) {
      // Open draft in compose modal
      const emailId = email.uid || email.id || email.seqno;
      // Use email's actual folder if available (for label views), otherwise use mailboxPath
      const emailFolder = email.folder || mailboxPath;
      openDraft(emailId, emailFolder, emailId);
    } else {
      // Navigate to email detail route
      const emailId = email.uid || email.id || email.seqno;
      // When viewing labels, use the email's actual folder instead of mailboxPath (which is "INBOX")
      // This ensures we navigate to the correct folder where the email actually exists
      const emailFolder = (isLabelRoute && email.folder) ? email.folder : mailboxPath;
      navigate(`/mailboxes/${encodeURIComponent(emailFolder)}/${emailId}`);
    }
  };

  const handleFlagChange = async (email, addFlags = [], removeFlags = []) => {
    try {
      const emailId = email.uid || email.id || email.seqno;
      const sourceFolder = email?.folder || mailboxPath;
      
      // Check if read status is changing
      const wasUnreadBefore = !email.isRead;
      let isUnreadAfter = wasUnreadBefore;
      
      if (addFlags.includes("\\Seen")) {
        isUnreadAfter = false;
      }
      if (removeFlags.includes("\\Seen")) {
        isUnreadAfter = true;
      }
      
      // Update local state
      setEmails(prevEmails =>
        prevEmails.map(e => {
          const updated = { ...e };
          if (parseInt(e.uid || e.id || e.seqno) === parseInt(emailId)) {
            if (addFlags.includes("\\Seen")) updated.isRead = true;
            if (removeFlags.includes("\\Seen")) updated.isRead = false;
            if (addFlags.includes("\\Flagged")) updated.isStarred = true;
            if (removeFlags.includes("\\Flagged")) updated.isStarred = false;
            return updated;
          }
          return e;
        })
      );

      await mailClientAPI.changeFlags(mailboxPath, emailId, addFlags, removeFlags);
      
      // Update mailbox counts if read status changed
      if (updateMailboxCounts && wasUnreadBefore !== isUnreadAfter) {
        if (isUnreadAfter) {
          // Changed to unread
          updateMailboxCounts({
            sourceFolder: sourceFolder,
            readStatusChange: 'unread',
            wasUnread: false,
            count: 1,
          });
        } else {
          // Changed to read
          updateMailboxCounts({
            sourceFolder: sourceFolder,
            readStatusChange: 'read',
            wasUnread: true,
            count: 1,
          });
        }
      }
    } catch (err) {
      console.error("Error changing flags:", err);
    }
  };

  const handleArchive = async (mailboxPath, emailId) => {
    try {
      // Find the email to get its read status before removing it
      const email = emails.find((e) => {
        const id = e.uid || e.id || e.seqno;
        return parseInt(id) === parseInt(emailId);
      });
      
      const wasUnread = email ? !email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      // Remove email from state instead of reloading
      setEmails((prevEmails) =>
        prevEmails.filter((email) => {
          const id = email.uid || email.id || email.seqno;
          return parseInt(id) !== parseInt(emailId);
        })
      );

      await mailClientAPI.archiveEmail(mailboxPath, emailId);
      
      // Update mailbox counts
      if (updateMailboxCounts) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          destinationFolder: "Archive",
          wasUnread: wasUnread,
          count: 1,
        });
      }
    } catch (error) {
      console.error("Error archiving email:", error);
      throw error;
    }
  };

  const handleDelete = async (mailboxPath, emailId) => {
    try {
      // Find the email to get its read status before removing it
      const email = emails.find((e) => {
        const id = e.uid || e.id || e.seqno;
        return parseInt(id) === parseInt(emailId);
      });
      
      const wasUnread = email ? !email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      // Remove email from state instead of reloading
      setEmails((prevEmails) =>
        prevEmails.filter((email) => {
          const id = email.uid || email.id || email.seqno;
          return parseInt(id) !== parseInt(emailId);
        })
      );

      await mailClientAPI.trashEmail(mailboxPath, emailId);
      
      // Update mailbox counts
      if (updateMailboxCounts) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          destinationFolder: "Trash",
          wasUnread: wasUnread,
          count: 1,
        });
      }
    } catch (error) {
      console.error("Error deleting email:", error);
      throw error;
    }
  };

  const handleRestore = async (mailboxPath, emailId) => {
    try {
      // Find the email to get its read status before removing it
      const email = emails.find((e) => {
        const id = e.uid || e.id || e.seqno;
        return parseInt(id) === parseInt(emailId);
      });
      
      const wasUnread = email ? !email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      // Remove email from state instead of reloading
      setEmails((prevEmails) =>
        prevEmails.filter((email) => {
          const id = email.uid || email.id || email.seqno;
          return parseInt(id) !== parseInt(emailId);
        })
      );

      await mailClientAPI.restoreEmail(mailboxPath, emailId);
      
      // Update mailbox counts
      if (updateMailboxCounts) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          destinationFolder: "INBOX",
          wasUnread: wasUnread,
          count: 1,
        });
      }
    } catch (error) {
      console.error("Error restoring email:", error);
      throw error;
    }
  };

  const handlePermanentDelete = async (mailboxPath, emailId) => {
    try {
      // Find the email to get its read status before removing it
      const email = emails.find((e) => {
        const id = e.uid || e.id || e.seqno;
        return parseInt(id) === parseInt(emailId);
      });
      
      const wasUnread = email ? !email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      // Remove email from state instead of reloading
      setEmails((prevEmails) =>
        prevEmails.filter((email) => {
          const id = email.uid || email.id || email.seqno;
          return parseInt(id) !== parseInt(emailId);
        })
      );

      await mailClientAPI.permanentDeleteEmail(mailboxPath, emailId);
      
      // Update mailbox counts (permanent delete - no destination)
      if (updateMailboxCounts) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          wasUnread: wasUnread,
          count: 1,
        });
      }
    } catch (error) {
      console.error("Error permanently deleting email:", error);
      throw error;
    }
  };

  const handleMarkAsRead = async (mailboxPath, emailId) => {
    try {
      // Find the email to check if it was unread
      const email = emails.find((e) => {
        const id = e.uid || e.id || e.seqno;
        return parseInt(id) === parseInt(emailId);
      });
      
      const wasUnread = email ? !email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      // Update local state
      setEmails((prevEmails) =>
        prevEmails.map((email) => {
          const id = email.uid || email.id || email.seqno;
          return parseInt(id) === parseInt(emailId) ? { ...email, isRead: true } : email;
        })
      );

      await mailClientAPI.markAsRead(mailboxPath, emailId);
      
      // Update mailbox counts (only if it was unread)
      if (updateMailboxCounts && wasUnread) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          readStatusChange: 'read',
          wasUnread: true,
          count: 1,
        });
      }
    } catch (error) {
      console.error("Error marking email as read:", error);
      throw error;
    }
  };

  const handleMarkAsUnread = async (mailboxPath, emailId) => {
    try {
      // Find the email to check if it was read
      const email = emails.find((e) => {
        const id = e.uid || e.id || e.seqno;
        return parseInt(id) === parseInt(emailId);
      });
      
      const wasRead = email ? email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      // Update local state
      setEmails((prevEmails) =>
        prevEmails.map((email) => {
          const id = email.uid || email.id || email.seqno;
          return parseInt(id) === parseInt(emailId) ? { ...email, isRead: false } : email;
        })
      );

      await mailClientAPI.markAsUnread(mailboxPath, emailId);
      
      // Update mailbox counts (only if it was read)
      if (updateMailboxCounts && wasRead) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          readStatusChange: 'unread',
          wasUnread: false, // was read, now unread
          count: 1,
        });
      }
    } catch (error) {
      console.error("Error marking email as unread:", error);
      throw error;
    }
  };

  const handleRefresh = async () => {
    // Show refreshing notification
    const refreshKey = `refresh-${Date.now()}`;
    notification.info({
      key: refreshKey,
      message: "Refreshing...",
      description: isSearchRoute ? "Refreshing search results" : isLabelRoute ? "Refreshing label view" : "Updating your mailbox",
      duration: 0, // Don't auto-close, we'll close it manually
      placement: "bottomLeft",
    });

    // Track the mailbox path for this refresh request
    const expectedMailboxPath = mailboxPath;
    const expectedIsSearch = isSearchRoute;
    const expectedIsLabel = isLabelRoute;
    const expectedLabelId = labelId;
    currentMailboxRef.current = expectedMailboxPath;

    // Create searchParams inside the function to avoid dependency issues
    const currentSearchParams = new URLSearchParams(location.search);
    
    try {
      let response;
      
      if (expectedIsSearch) {
        // Refresh search results with same search parameters
        const searchParamsObj = {
          q: currentSearchParams.get("q") || "",
          from: currentSearchParams.get("from") || "",
          to: currentSearchParams.get("to") || "",
          subject: currentSearchParams.get("subject") || "",
        };
        const page = parseInt(currentSearchParams.get("p")) || 1;
        
        response = await mailClientAPI.searchMessages(expectedMailboxPath, searchParamsObj, page, 50);
      } else if (expectedIsLabel && expectedLabelId) {
        // Refresh label view
        response = await mailClientAPI.getMessages(expectedMailboxPath, 1, 50, expectedLabelId);
      } else {
        // Regular message refresh
        response = await mailClientAPI.getMessages(expectedMailboxPath, 1, 50);
      }
      
      // Only update state if this response is for the current mailbox
      if (currentMailboxRef.current === expectedMailboxPath) {
        const messages = response.Output?.messages || [];
        const transformedMessages = messages?.map?.((msg) => ({
          ...msg,
          id: msg.id || msg.uid?.toString() || msg.seqno?.toString(),
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(msg.date || Date.now()),
          // Preserve the original folder from API (important for label views where emails can be from any folder)
          // Only use expectedMailboxPath if folder is not provided by API
          folder: msg.folder || expectedMailboxPath.toLowerCase(),
          labels: msg.labels || [],
        }));
        setEmails(transformedMessages || []);
        
        // Close refreshing notification and show success
        notification.destroy(refreshKey);
        notification.success({
          message: "Refreshed",
          description: expectedIsSearch ? "Search results updated" : expectedIsLabel ? "Label view updated" : "Your mailbox has been updated",
          duration: 2,
          placement: "bottomLeft",
        });
      } else {
        console.log("Ignoring stale refresh response for mailbox:", expectedMailboxPath);
        notification.destroy(refreshKey);
      }
    } catch (error) {
      // Only show error if this is still the current mailbox
      if (currentMailboxRef.current === expectedMailboxPath) {
        console.error("Error refreshing messages:", error);
        
        // Close refreshing notification and show error
        notification.close(refreshKey);
        notification.error({
          message: "Refresh failed",
          description: expectedIsSearch ? "Failed to refresh search results. Please try again." : "Failed to refresh your mailbox. Please try again.",
          duration: 3,
          placement: "bottomLeft",
        });
      } else {
        notification.destroy(refreshKey);
      }
    }
  };

  // Show loading only when loading messages (not when loading mailboxes)
  // This ensures we only show loading on the mailbox list page
  if (loading) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar Skeleton */}
        <div className="flex items-center px-4 py-2 border-b border-gray-200 bg-white">
          <Skeleton.Button active size="small" style={{ width: 16, height: 16 }} />
          <Skeleton.Button active size="small" style={{ width: 20, height: 20, marginLeft: 8 }} />
          <Skeleton.Button active size="small" style={{ width: 20, height: 20, marginLeft: 8 }} />
          <Skeleton.Button active size="small" style={{ width: 20, height: 20, marginLeft: 8 }} />
        </div>

        {/* Email List Skeleton */}
        <div className="flex-1 overflow-y-auto">
          {[...Array(10)].map((_, index) => (
            <div
              key={index}
              className="flex items-center px-4 py-3 border-b border-gray-100"
            >
              {/* Checkbox and Star */}
              <div className="flex items-center space-x-3 mr-4">
                <Skeleton.Button active size="small" style={{ width: 16, height: 16 }} />
                <Skeleton.Button active size="small" style={{ width: 20, height: 20 }} />
              </div>

              {/* Sender */}
              <div className="w-48 mr-4">
                <Skeleton.Input active size="small" style={{ width: 120, height: 16 }} />
              </div>

              {/* Subject & Preview */}
              <div className="flex-1 flex items-center">
                <Skeleton.Input active size="small" style={{ width: "60%", height: 16, marginRight: 8 }} />
                <Skeleton.Input active size="small" style={{ width: "35%", height: 14 }} />
              </div>

              {/* Time */}
              <div className="w-24 text-right ml-4">
                <Skeleton.Input active size="small" style={{ width: 60, height: 12, marginLeft: "auto" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Expose draft update functions to parent via useOutletContext
  // We'll use a custom hook pattern or pass via a ref
  // For now, let's use a simpler approach - pass callbacks via a custom context provider
  // Actually, let's use a simpler approach: pass the callbacks directly to ComposeModal via a wrapper
  
  // Get folder name for title
  const getFolderName = (mailboxPath, isLabel, labelId) => {
    if (isLabel && labelId) {
      // Find the label by ID
      const label = labels.find(l => l.id === labelId);
      return label ? label.name : `Label #${labelId}`;
    }
    const path = mailboxPath.toLowerCase();
    if (path === "inbox") return "Inbox";
    if (path.includes("sent")) return "Sent";
    if (path.includes("draft")) return "Drafts";
    if (path.includes("archive")) return "Archive";
    if (path.includes("junk") || path.includes("spam")) return "Spam";
    if (path.includes("trash")) return "Trash";
    // Capitalize first letter of mailbox name
    const parts = mailboxPath.split(/[./]/);
    const name = parts[parts.length - 1] || mailboxPath;
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const folderName = getFolderName(mailboxPath, isLabelRoute, labelId);
  const userEmail = userInfo?.email || "";

  return (
    <>
      <Helmet>
        <title>{folderName} - {userEmail || "Grigora Mail"}</title>
      </Helmet>
      <EmailList
        emails={filteredEmails}
        selectedFolder={mailboxPath}
        onSelectEmail={handleSelectEmail}
        onFlagChange={handleFlagChange}
        mailboxPath={mailboxPath}
        onArchive={handleArchive}
        onDelete={handleDelete}
        onMarkAsRead={handleMarkAsRead}
        onMarkAsUnread={handleMarkAsUnread}
        onRefresh={handleRefresh}
        onRestore={handleRestore}
        onPermanentDelete={handlePermanentDelete}
        searchQuery={searchQueryForHighlight}
        labels={labels}
      />
    </>
  );
}

export default MailboxView;

